import { alpha, Avatar, Chip, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { getInitials } from '../../utils/helper';
import DoneIcon from '@mui/icons-material/Done';
import PersonIcon from '@mui/icons-material/Person';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import MessageIcon from '@mui/icons-material/Message';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import StyleIcon from '@mui/icons-material/Style';
import LabelIcon from '@mui/icons-material/Label';
import AddIcon from '@mui/icons-material/Add';
import { KanbanType, ResponsiblePersonVisibleForTypes } from '../../config/kanban';
import { TagType } from '../../config/tag';

const SUB_MENU = {
  CHIEF: 'CHIEF',
  OTHER_RESP: 'OTHER_RESP',
  PRIORITY: 'PRIORITY',
  LABEL: 'LABEL',
  STICKER: 'STICKER',
};

const CardMenu = ({
  handleClose,
  menuPosition,
  open,
  getItemById,
  handleDeleteItemByType,
  autoCompleteData,
  addOrRemoveRespPerson,
  currentItem,
  updateRespPerson,
  type,
  handleOpenColorDialog,
  handleOpenCommentDialog,
  updatePriorityByType,
  tags,
  updateTagByType,
  setAddDialogData,
}) => {
  const defaultSubMenu = {
    target: null,
    type: null,
  };

  const [subMenu, setSubMenu] = useState(defaultSubMenu);

  const handleOpenSubMenu = (target, subMenuType) => {
    setSubMenu({
      target,
      type: subMenuType,
    });
  };

  const handleCloseSubMenu = () => {
    setSubMenu(defaultSubMenu);
  };

  const labels = tags.filter((item) => item.tagType === TagType.LABEL);
  const stickers = tags.filter((item) => item.tagType === TagType.STICKER);

  const subMenuConfigs = {
    [SUB_MENU.CHIEF]: {
      title: 'Fő felelős',
      items: autoCompleteData?.user ?? [],
      getKey: (u) => u.userId,
      renderLabel: (u) => u.nickName,
      getChecked: (u) =>
        type === KanbanType.TASK
          ? currentItem?.resposiblePersonId === u.userId
          : currentItem?.responsiblePersonId === u.userId,
      onClick: (u, checked) => updateRespPerson(checked ? null : u.userId),
      renderStartAdornment: (u) => (
        <Avatar
          sx={{
            bgcolor: '#1185f2',
            width: 30,
            height: 30,
            fontSize: '1rem',
            mr: 2,
          }}
        >
          {getInitials(u.nickName)}
        </Avatar>
      ),
    },

    [SUB_MENU.OTHER_RESP]: {
      title: 'További felelősök',
      items: autoCompleteData?.user ?? [],
      getKey: (u) => u.userId,
      renderLabel: (u) => u.nickName,
      getChecked: (u) => currentItem?.respPersonlist?.some((p) => p.userId === u.userId),
      onClick: (u) => addOrRemoveRespPerson(u.userId),
      renderStartAdornment: (u) => (
        <Avatar
          sx={{
            bgcolor: '#F9BC04',
            width: 30,
            height: 30,
            fontSize: '1rem',
            mr: 2,
          }}
        >
          {getInitials(u.nickName)}
        </Avatar>
      ),
    },

    [SUB_MENU.PRIORITY]: {
      title: 'Prioritás',
      items: autoCompleteData?.priority ?? [],
      getKey: (p) => p.itemId,
      renderLabel: (p) => p.itemName,
      getChecked: (p) => currentItem?.priorityId === p.itemId || currentItem?.priorId === p.itemId,
      onClick: (p) => updatePriorityByType(p.itemId),
    },

    [SUB_MENU.LABEL]: {
      title: 'Címke',
      items: labels,
      getKey: (p) => p.id,
      renderLabel: (p) => (
        <Chip
          label={p.name}
          sx={{
            bgcolor: alpha(p.color, 0.2),
            color: p.color,
            borderRadius: '5px',
            fontWeight: 'bold',

            '&:hover': {
              bgcolor: alpha(p.color, 0.3),
            },
          }}
        />
      ),
      getChecked: (tag) => currentItem?.tagLink?.some((link) => link.tagId === tag.id),
      onClick: (p) => updateTagByType(p.id),
    },

    [SUB_MENU.STICKER]: {
      title: 'Matrica',
      items: stickers,
      getKey: (p) => p.id,
      renderLabel: (p) => p.name,
      getChecked: (tag) => currentItem?.tagLink?.some((link) => link.tagId === tag.id),
      onClick: (p) => updateTagByType(p.id),
      renderStartAdornment: (u) => (
        <Avatar
          sx={{
            width: 30,
            height: 30,
            fontSize: '1rem',
            mr: 2,
          }}
          src={`${process.env.REACT_APP_API_URL}/${u.picture}`}
        >
          {getInitials(u.name)}
        </Avatar>
      ),
    },
  };

  const activeSubMenu = subMenu.type ? subMenuConfigs[subMenu.type] : null;

  return (
    <>
      <Menu
        id="basic-menu"
        open={open}
        onClose={() => {
          handleClose();
          handleCloseSubMenu();
        }}
        anchorReference="anchorPosition"
        anchorPosition={Boolean(menuPosition) ? { top: menuPosition.mouseY, left: menuPosition.mouseX } : undefined}
      >
        <MenuItem onClick={() => getItemById(currentItem.id)} onMouseEnter={handleCloseSubMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Szerkesztés</ListItemText>
        </MenuItem>
        {type === KanbanType.PROJECT && (
          <MenuItem
            onClick={() => {
              setAddDialogData({
                open: true,
                dialogData: { projectId: currentItem.projectId },
                overrideType: KanbanType.TASK,
              });
              handleClose();
            }}
            onMouseEnter={handleCloseSubMenu}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Feladat</ListItemText>
          </MenuItem>
        )}
        {ResponsiblePersonVisibleForTypes.includes(type) && (
          <>
            <MenuItem onMouseEnter={(e) => handleOpenSubMenu(e.currentTarget, SUB_MENU.CHIEF)}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Fő felelős</ListItemText>
              <ArrowRightIcon fontSize="small" />
            </MenuItem>
            <MenuItem onMouseEnter={(e) => handleOpenSubMenu(e.currentTarget, SUB_MENU.OTHER_RESP)}>
              <ListItemIcon>
                <PeopleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>További felelősök</ListItemText>
              <ArrowRightIcon fontSize="small" />
            </MenuItem>
          </>
        )}
        <MenuItem onClick={handleOpenColorDialog} onMouseEnter={handleCloseSubMenu}>
          <ListItemIcon>
            <ColorLensIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Színválasztó</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenCommentDialog} onMouseEnter={handleCloseSubMenu}>
          <ListItemIcon>
            <MessageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Hozzászólások</ListItemText>
        </MenuItem>
        {autoCompleteData?.priority?.length > 0 && (
          <MenuItem onMouseEnter={(e) => handleOpenSubMenu(e.currentTarget, SUB_MENU.PRIORITY)}>
            <ListItemIcon>
              <LowPriorityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Prioritás</ListItemText>
            <ArrowRightIcon fontSize="small" />
          </MenuItem>
        )}
        {labels.length > 0 && (
          <MenuItem onMouseEnter={(e) => handleOpenSubMenu(e.currentTarget, SUB_MENU.LABEL)}>
            <ListItemIcon>
              <LabelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Címke</ListItemText>
            <ArrowRightIcon fontSize="small" />
          </MenuItem>
        )}
        {stickers.length > 0 && (
          <MenuItem onMouseEnter={(e) => handleOpenSubMenu(e.currentTarget, SUB_MENU.STICKER)}>
            <ListItemIcon>
              <StyleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Matrica</ListItemText>
            <ArrowRightIcon fontSize="small" />
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleDeleteItemByType} onMouseEnter={handleCloseSubMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Törlés</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={subMenu.target}
        open={Boolean(subMenu.target && activeSubMenu)}
        onClose={handleCloseSubMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ pointerEvents: 'none' }}
        MenuListProps={{
          sx: { pointerEvents: 'auto' },
          onMouseLeave: handleCloseSubMenu,
        }}
      >
        <Typography fontSize="large" textAlign="center" my={1}>
          {activeSubMenu?.title}
        </Typography>
        {activeSubMenu?.items.map((item) => {
          const checked = activeSubMenu.getChecked?.(item);
          return (
            <MenuItem key={activeSubMenu.getKey(item)} onClick={() => activeSubMenu.onClick(item, checked)}>
              <ListItemIcon>{checked && <DoneIcon fontSize="small" />}</ListItemIcon>
              {activeSubMenu.renderStartAdornment?.(item)}
              <ListItemText>{activeSubMenu.renderLabel(item)}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default CardMenu;
